import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/utils/api';
import { applyBranding, isValidHex, isValidFontName } from '@/contexts/BrandingProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Save, RotateCcw, Plus, Trash2, Upload, ImageIcon, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ------------------ Reusable field components ------------------ */

const TextField = ({ label, value, onChange, placeholder, type = 'text', testId }) => (
  <div className="space-y-1">
    <Label className="text-white text-sm">{label}</Label>
    <Input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-[#080808] border-[#D4AF37]/30 text-white"
      data-testid={testId}
    />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 3, testId }) => (
  <div className="space-y-1">
    <Label className="text-white text-sm">{label}</Label>
    <Textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="bg-[#080808] border-[#D4AF37]/30 text-white"
      data-testid={testId}
    />
  </div>
);

const ToggleField = ({ label, description, checked, onChange, testId }) => (
  <div className="flex items-center justify-between bg-[#080808] border border-[#D4AF37]/10 rounded-lg p-3">
    <div>
      <Label className="text-white text-sm">{label}</Label>
      {description && <p className="text-xs text-[#A0A0A0] mt-0.5">{description}</p>}
    </div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const ImagePicker = ({ label, value, onChange, testId }) => {
  const fileRef = useRef();
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('alt_text', label);
    try {
      const { data } = await api.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${API_URL}${data.url}`;
      onChange(fullUrl);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Upload failed');
    }
    e.target.value = '';
  };
  return (
    <div className="space-y-2">
      <Label className="text-white text-sm">{label}</Label>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#D4AF37]/30">
            <img src={value} alt={label} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
              data-testid={`${testId}-remove`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-lg border-2 border-dashed border-[#D4AF37]/30 flex items-center justify-center text-[#A0A0A0]">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid={`${testId}-input`} />
          <Button type="button" size="sm" onClick={() => fileRef.current?.click()} className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]" data-testid={`${testId}-upload`}>
            <Upload className="w-3 h-3 mr-2" />
            {value ? 'Replace' : 'Upload'}
          </Button>
          <Input
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="or paste URL"
            className="bg-[#080808] border-[#D4AF37]/30 text-white text-xs h-8 w-52"
            data-testid={testId}
          />
        </div>
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, onSave, onReset, saving, sectionKey }) => (
  <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6 mb-6">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-bold text-white">{title}</h2>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onReset}
          className="border-[#A0A0A0]/30 text-[#A0A0A0] hover:bg-[#080808]"
          data-testid={`btn-reset-${sectionKey}`}
        >
          <RotateCcw className="w-3 h-3 mr-2" />
          Reset
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]"
          data-testid={`btn-save-${sectionKey}`}
        >
          <Save className="w-3 h-3 mr-2" />
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </Card>
);

/* ------------------ Main page ------------------ */

const ContentManagement = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [resetKey, setResetKey] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const update = (key, updater) => {
    setSettings((prev) => ({ ...prev, [key]: typeof updater === 'function' ? updater(prev[key]) : updater }));
  };

  const saveSection = async (key) => {
    // Validate branding before saving
    if (key === 'branding') {
      const b = settings.branding || {};
      const invalidColors = ['primary_gold', 'pink_accent', 'background', 'card_bg', 'text_primary', 'text_secondary']
        .filter((k) => b[k] && !isValidHex(b[k]));
      if (invalidColors.length) {
        toast.error(`Invalid hex colour(s): ${invalidColors.join(', ')}. Use #rgb, #rrggbb or #rrggbbaa.`);
        return;
      }
      if (b.heading_font && !isValidFontName(b.heading_font)) {
        toast.error('Invalid heading font. Letters, digits, spaces and hyphens only.');
        return;
      }
      if (b.body_font && !isValidFontName(b.body_font)) {
        toast.error('Invalid body font. Letters, digits, spaces and hyphens only.');
        return;
      }
    }
    setSaving(key);
    try {
      await api.put(`/settings/${key}`, { value: settings[key] });
      if (key === 'branding') {
        applyBranding(settings.branding);
      }
      toast.success(`${key} saved`);
    } catch (error) {
      toast.error(`Failed to save ${key}`);
    } finally {
      setSaving('');
    }
  };

  const confirmReset = async () => {
    if (!resetKey) return;
    try {
      const { data } = await api.post(`/settings/${resetKey}/reset`);
      setSettings((s) => ({ ...s, [resetKey]: data.value }));
      if (resetKey === 'branding') {
        applyBranding(data.value);
      }
      toast.success(`${resetKey} reset to default`);
    } catch (error) {
      toast.error('Failed to reset');
    } finally {
      setResetKey(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#080808] flex items-center justify-center text-white">Loading content...</div>;
  }

  const hero = settings.hero || {};
  const countdown = settings.countdown || {};
  const stats = settings.statistics || {};
  const checker = settings.entry_checker || {};
  const howto = settings.how_to_join || { steps: [] };
  const rulesSec = settings.rules_section || { items: [] };
  const prizesSec = settings.prizes_section || {};
  const winnersSec = settings.winners_section || {};
  const footer = settings.footer || {};
  const branding = settings.branding || {};
  const seo = settings.seo || {};

  return (
    <div className="min-h-screen bg-[#080808] pb-12">
      <div className="bg-[#121212] border-b border-[#D4AF37]/20 px-6 py-4 mb-6">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37]">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </Link>
          <h1 className="text-xl font-bold text-white">Content Management</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#121212] border border-[#D4AF37]/10 h-auto flex-wrap p-2 mb-6 justify-start" data-testid="content-tabs">
            {[
              ['hero', 'Hero'], ['countdown', 'Countdown'], ['entry_checker', 'Entry Checker'],
              ['statistics', 'Statistics'], ['prizes_section', 'Prizes'], ['how_to_join', 'How to Join'],
              ['rules_section', 'Rules'], ['winners_section', 'Winners'], ['branding', 'Branding'],
              ['footer', 'Footer'], ['seo', 'SEO'], ['media', 'Media'],
            ].map(([k, label]) => (
              <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#080808] text-white text-xs sm:text-sm" data-testid={`tab-${k}`}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* HERO */}
          <TabsContent value="hero">
            <SectionCard title="Hero Section" sectionKey="hero" saving={saving === 'hero'} onSave={() => saveSection('hero')} onReset={() => setResetKey('hero')}>
              <TextField label="Main Title" value={hero.title} onChange={(v) => update('hero', (h) => ({ ...h, title: v }))} testId="input-hero-title" />
              <TextField label="Subtitle (gold)" value={hero.subtitle} onChange={(v) => update('hero', (h) => ({ ...h, subtitle: v }))} testId="input-hero-subtitle" />
              <TextField label="Year" value={hero.year} onChange={(v) => update('hero', (h) => ({ ...h, year: v }))} testId="input-hero-year" />
              <TextArea label="Tagline" value={hero.tagline} onChange={(v) => update('hero', (h) => ({ ...h, tagline: v }))} testId="input-hero-tagline" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField label="Check Entries button text" value={hero.button_check_text} onChange={(v) => update('hero', (h) => ({ ...h, button_check_text: v }))} testId="input-btn-check-text" />
                <TextField label="Check Entries target ID" value={hero.button_check_target} onChange={(v) => update('hero', (h) => ({ ...h, button_check_target: v }))} />
                <TextField label="Top Up button text" value={hero.button_topup_text} onChange={(v) => update('hero', (h) => ({ ...h, button_topup_text: v }))} />
              </div>

              <div className="pt-4 mt-4 border-t border-[#D4AF37]/10 space-y-4">
                <h3 className="text-white font-semibold text-sm">Hero background image</h3>
                <ToggleField
                  label="Enable background image"
                  description="When off, the Hero keeps the current solid black background."
                  checked={hero.image_enabled}
                  onChange={(v) => update('hero', (h) => ({ ...h, image_enabled: v }))}
                  testId="toggle-hero-image-enabled"
                />
                <ImagePicker label="Desktop image" value={hero.desktop_image_url} onChange={(v) => update('hero', (h) => ({ ...h, desktop_image_url: v }))} testId="hero-desktop-image" />
                <ImagePicker label="Mobile image (falls back to desktop when empty)" value={hero.mobile_image_url} onChange={(v) => update('hero', (h) => ({ ...h, mobile_image_url: v }))} testId="hero-mobile-image" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-white text-sm">Background position</Label>
                    <select
                      value={hero.bg_position || 'center'}
                      onChange={(e) => update('hero', (h) => ({ ...h, bg_position: e.target.value }))}
                      className="w-full bg-[#080808] border border-[#D4AF37]/30 text-white rounded-md h-10 px-3"
                      data-testid="select-bg-position"
                    >
                      {['center', 'top', 'bottom', 'left', 'right', 'top left', 'top right', 'bottom left', 'bottom right'].map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-white text-sm">Background size</Label>
                    <select
                      value={hero.bg_size || 'cover'}
                      onChange={(e) => update('hero', (h) => ({ ...h, bg_size: e.target.value }))}
                      className="w-full bg-[#080808] border border-[#D4AF37]/30 text-white rounded-md h-10 px-3"
                      data-testid="select-bg-size"
                    >
                      {['cover', 'contain', 'auto'].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <TextField label="Overlay colour (hex)" value={hero.overlay_color} onChange={(v) => update('hero', (h) => ({ ...h, overlay_color: v }))} testId="input-overlay-color" />
                  <div className="space-y-1">
                    <Label className="text-white text-sm">Overlay opacity ({typeof hero.overlay_opacity === 'number' ? hero.overlay_opacity.toFixed(2) : '0.60'})</Label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={hero.overlay_opacity ?? 0.6}
                      onChange={(e) => update('hero', (h) => ({ ...h, overlay_opacity: parseFloat(e.target.value) }))}
                      className="w-full accent-[#D4AF37]"
                      data-testid="input-overlay-opacity"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 mt-4 border-t border-[#D4AF37]/10">
                <ImagePicker label="Logo (optional)" value={hero.logo_url} onChange={(v) => update('hero', (h) => ({ ...h, logo_url: v }))} testId="hero-logo" />
              </div>
            </SectionCard>

            <SectionCard title="Event basics (dates & Room ID)" sectionKey="event_dates" saving={false}
              onSave={async () => {
                setSaving('event_dates');
                try {
                  await Promise.all([
                    api.put('/settings/room_id', { value: settings.room_id }),
                    api.put('/settings/campaign_start', { value: settings.campaign_start }),
                    api.put('/settings/campaign_end', { value: settings.campaign_end }),
                    api.put('/settings/live_draw_date', { value: settings.live_draw_date }),
                    api.put('/settings/topup_url', { value: settings.topup_url }),
                  ]);
                  toast.success('Event basics saved');
                } catch (e) { toast.error('Failed'); }
                setSaving('');
              }}
              onReset={() => toast.info('Reset each field individually or ignore')}
            >
              <TextField label="Room ID" value={settings.room_id} onChange={(v) => setSettings((s) => ({ ...s, room_id: v }))} testId="input-room-id" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField type="date" label="Campaign start" value={settings.campaign_start} onChange={(v) => setSettings((s) => ({ ...s, campaign_start: v }))} />
                <TextField type="date" label="Campaign end" value={settings.campaign_end} onChange={(v) => setSettings((s) => ({ ...s, campaign_end: v }))} />
              </div>
              <TextField type="datetime-local" label="Live draw date & time" value={(settings.live_draw_date || '').slice(0, 16)} onChange={(v) => setSettings((s) => ({ ...s, live_draw_date: v }))} />
              <TextField type="url" label="Top-Up URL" value={settings.topup_url} onChange={(v) => setSettings((s) => ({ ...s, topup_url: v }))} testId="input-topup-url" />
            </SectionCard>
          </TabsContent>

          {/* COUNTDOWN */}
          <TabsContent value="countdown">
            <SectionCard title="Countdown Settings" sectionKey="countdown" saving={saving === 'countdown'} onSave={() => saveSection('countdown')} onReset={() => setResetKey('countdown')}>
              <ToggleField label="Enable countdown" checked={countdown.enabled} onChange={(v) => update('countdown', (c) => ({ ...c, enabled: v }))} testId="toggle-countdown-enabled" />
              <div className="grid grid-cols-2 gap-3">
                {['days', 'hours', 'minutes', 'seconds'].map((k) => (
                  <TextField key={k} label={`Label: ${k}`} value={countdown.labels?.[k]}
                    onChange={(v) => update('countdown', (c) => ({ ...c, labels: { ...(c.labels || {}), [k]: v } }))} />
                ))}
              </div>
              <TextField label="Expired message" value={countdown.expired_message} onChange={(v) => update('countdown', (c) => ({ ...c, expired_message: v }))} />
              <TextField label="Time zone" value={countdown.timezone} onChange={(v) => update('countdown', (c) => ({ ...c, timezone: v }))} />
            </SectionCard>
          </TabsContent>

          {/* ENTRY CHECKER */}
          <TabsContent value="entry_checker">
            <SectionCard title="Entry Checker" sectionKey="entry_checker" saving={saving === 'entry_checker'} onSave={() => saveSection('entry_checker')} onReset={() => setResetKey('entry_checker')}>
              <TextField label="Section title" value={checker.section_title} onChange={(v) => update('entry_checker', (c) => ({ ...c, section_title: v }))} />
              <TextArea label="Instruction" value={checker.instruction} onChange={(v) => update('entry_checker', (c) => ({ ...c, instruction: v }))} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="Input placeholder" value={checker.placeholder} onChange={(v) => update('entry_checker', (c) => ({ ...c, placeholder: v }))} />
                <TextField label="Search button text" value={checker.search_button} onChange={(v) => update('entry_checker', (c) => ({ ...c, search_button: v }))} />
                <TextField label="Success message" value={checker.success_message} onChange={(v) => update('entry_checker', (c) => ({ ...c, success_message: v }))} />
                <TextField label="Not-found message" value={checker.not_found_message} onChange={(v) => update('entry_checker', (c) => ({ ...c, not_found_message: v }))} />
                <TextField label="Invalid message" value={checker.invalid_message} onChange={(v) => update('entry_checker', (c) => ({ ...c, invalid_message: v }))} />
                <TextField label="Error message" value={checker.error_message} onChange={(v) => update('entry_checker', (c) => ({ ...c, error_message: v }))} />
                <TextField label="Label: Sugo ID" value={checker.label_sugo_id} onChange={(v) => update('entry_checker', (c) => ({ ...c, label_sugo_id: v }))} />
                <TextField label="Label: Username" value={checker.label_username} onChange={(v) => update('entry_checker', (c) => ({ ...c, label_username: v }))} />
                <TextField label="Label: Total" value={checker.label_total} onChange={(v) => update('entry_checker', (c) => ({ ...c, label_total: v }))} />
                <TextField label="Label: Active" value={checker.label_active} onChange={(v) => update('entry_checker', (c) => ({ ...c, label_active: v }))} />
                <TextField label="Label: Used" value={checker.label_used} onChange={(v) => update('entry_checker', (c) => ({ ...c, label_used: v }))} />
                <TextField label="Label: Wins" value={checker.label_wins} onChange={(v) => update('entry_checker', (c) => ({ ...c, label_wins: v }))} />
              </div>
              <ToggleField label="Show entry history" checked={checker.show_history} onChange={(v) => update('entry_checker', (c) => ({ ...c, show_history: v }))} />
              <ToggleField label="Show username" checked={checker.show_username} onChange={(v) => update('entry_checker', (c) => ({ ...c, show_username: v }))} />
              <ToggleField label="Show latest entry date" checked={checker.show_latest_date} onChange={(v) => update('entry_checker', (c) => ({ ...c, show_latest_date: v }))} />
              <ToggleField label="Mask Sugo IDs" checked={checker.mask_ids} onChange={(v) => update('entry_checker', (c) => ({ ...c, mask_ids: v }))} />
            </SectionCard>
          </TabsContent>

          {/* STATISTICS */}
          <TabsContent value="statistics">
            <SectionCard title="Statistics Section" sectionKey="statistics" saving={saving === 'statistics'} onSave={() => saveSection('statistics')} onReset={() => setResetKey('statistics')}>
              <ToggleField label="Enable Statistics section" checked={stats.enabled} onChange={(v) => update('statistics', (s) => ({ ...s, enabled: v }))} />
              {['total_participants', 'total_entries', 'remaining_entries', 'total_winners'].map((k) => (
                <div key={k} className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end pt-2 border-t border-[#D4AF37]/10">
                  <TextField label={`Label: ${k.replace(/_/g, ' ')}`} value={stats.labels?.[k]}
                    onChange={(v) => update('statistics', (s) => ({ ...s, labels: { ...(s.labels || {}), [k]: v } }))} />
                  <ToggleField label={`Show ${k.replace(/_/g, ' ')}`} checked={stats.visibility?.[k]}
                    onChange={(v) => update('statistics', (s) => ({ ...s, visibility: { ...(s.visibility || {}), [k]: v } }))} />
                </div>
              ))}
            </SectionCard>
          </TabsContent>

          {/* PRIZES SECTION */}
          <TabsContent value="prizes_section">
            <SectionCard title="Prizes Section" sectionKey="prizes_section" saving={saving === 'prizes_section'} onSave={() => saveSection('prizes_section')} onReset={() => setResetKey('prizes_section')}>
              <TextField label="Section title" value={prizesSec.section_title} onChange={(v) => update('prizes_section', (p) => ({ ...p, section_title: v }))} />
              <TextField label="Instruction" value={prizesSec.instruction} onChange={(v) => update('prizes_section', (p) => ({ ...p, instruction: v }))} />
              <TextField label="'Left' wording" value={prizesSec.left_label} onChange={(v) => update('prizes_section', (p) => ({ ...p, left_label: v }))} />
              <ToggleField label="Show remaining quantity publicly" checked={prizesSec.show_remaining} onChange={(v) => update('prizes_section', (p) => ({ ...p, show_remaining: v }))} />
              <p className="text-sm text-[#A0A0A0] pt-2 border-t border-[#D4AF37]/10">
                Individual prize name, description, image, quantity are managed from{' '}
                <Link to="/admin/prizes" className="text-[#D4AF37] underline">Prize Management</Link>.
              </p>
            </SectionCard>
          </TabsContent>

          {/* HOW TO JOIN */}
          <TabsContent value="how_to_join">
            <SectionCard title="How to Join" sectionKey="how_to_join" saving={saving === 'how_to_join'} onSave={() => saveSection('how_to_join')} onReset={() => setResetKey('how_to_join')}>
              <TextField label="Section title" value={howto.section_title} onChange={(v) => update('how_to_join', (h) => ({ ...h, section_title: v }))} testId="input-howto-title" />
              <TextArea label="Clarification (below steps)" value={howto.clarification} rows={3}
                onChange={(v) => update('how_to_join', (h) => ({ ...h, clarification: v }))} />
              <div className="space-y-3">
                {(howto.steps || []).map((step, idx) => (
                  <Card key={idx} className="bg-[#080808] border border-[#D4AF37]/10 p-4" data-testid={`howto-step-card-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#D4AF37] font-bold">Step {step.order || idx + 1}</span>
                      <div className="flex items-center gap-2">
                        <Switch checked={step.enabled !== false} onCheckedChange={(v) => {
                          const steps = [...howto.steps];
                          steps[idx] = { ...steps[idx], enabled: v };
                          update('how_to_join', (h) => ({ ...h, steps }));
                        }} />
                        <Button size="sm" variant="outline" className="border-red-500/30 text-red-500"
                          onClick={() => {
                            const steps = howto.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
                            update('how_to_join', (h) => ({ ...h, steps }));
                          }}
                          data-testid={`btn-remove-step-${idx}`}
                        ><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <TextField label="Title" value={step.title} onChange={(v) => {
                      const steps = [...howto.steps]; steps[idx] = { ...steps[idx], title: v };
                      update('how_to_join', (h) => ({ ...h, steps }));
                    }} testId={`input-step-title-${idx}`} />
                    <TextArea label="Description" value={step.description} onChange={(v) => {
                      const steps = [...howto.steps]; steps[idx] = { ...steps[idx], description: v };
                      update('how_to_join', (h) => ({ ...h, steps }));
                    }} testId={`input-step-desc-${idx}`} />
                    <TextField label="Order" type="number" value={step.order} onChange={(v) => {
                      const steps = [...howto.steps]; steps[idx] = { ...steps[idx], order: parseInt(v) || 1 };
                      update('how_to_join', (h) => ({ ...h, steps }));
                    }} />
                  </Card>
                ))}
                <Button type="button" variant="outline" onClick={() => {
                  const steps = [...(howto.steps || [])];
                  steps.push({ title: 'NEW STEP', description: '', enabled: true, order: steps.length + 1 });
                  update('how_to_join', (h) => ({ ...h, steps }));
                }} className="border-[#D4AF37]/30 text-[#D4AF37]" data-testid="btn-add-step">
                  <Plus className="w-4 h-4 mr-2" />Add Step
                </Button>
              </div>
            </SectionCard>
          </TabsContent>

          {/* RULES */}
          <TabsContent value="rules_section">
            <SectionCard title="Rules & Terms" sectionKey="rules_section" saving={saving === 'rules_section'} onSave={() => saveSection('rules_section')} onReset={() => setResetKey('rules_section')}>
              <TextField label="Section title" value={rulesSec.section_title} onChange={(v) => update('rules_section', (r) => ({ ...r, section_title: v }))} />
              <div className="space-y-3">
                {(rulesSec.items || []).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-[#080808] border border-[#D4AF37]/10 rounded-lg p-3" data-testid={`rule-item-${idx}`}>
                    <Switch checked={item.enabled !== false} onCheckedChange={(v) => {
                      const items = [...rulesSec.items]; items[idx] = { ...items[idx], enabled: v };
                      update('rules_section', (r) => ({ ...r, items }));
                    }} />
                    <Input value={item.text} onChange={(e) => {
                      const items = [...rulesSec.items]; items[idx] = { ...items[idx], text: e.target.value };
                      update('rules_section', (r) => ({ ...r, items }));
                    }} className="flex-1 bg-[#080808] border-[#D4AF37]/30 text-white" data-testid={`input-rule-${idx}`} />
                    <Input type="number" value={item.order} onChange={(e) => {
                      const items = [...rulesSec.items]; items[idx] = { ...items[idx], order: parseInt(e.target.value) || 1 };
                      update('rules_section', (r) => ({ ...r, items }));
                    }} className="w-16 bg-[#080808] border-[#D4AF37]/30 text-white" />
                    <Button size="sm" variant="outline" className="border-red-500/30 text-red-500" onClick={() => {
                      const items = rulesSec.items.filter((_, i) => i !== idx).map((r, i) => ({ ...r, order: i + 1 }));
                      update('rules_section', (r) => ({ ...r, items }));
                    }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={() => {
                  const items = [...(rulesSec.items || [])];
                  items.push({ text: 'New rule', enabled: true, order: items.length + 1 });
                  update('rules_section', (r) => ({ ...r, items }));
                }} className="border-[#D4AF37]/30 text-[#D4AF37]" data-testid="btn-add-rule">
                  <Plus className="w-4 h-4 mr-2" />Add Rule
                </Button>
              </div>
            </SectionCard>
          </TabsContent>

          {/* WINNERS SECTION */}
          <TabsContent value="winners_section">
            <SectionCard title="Winners Section" sectionKey="winners_section" saving={saving === 'winners_section'} onSave={() => saveSection('winners_section')} onReset={() => setResetKey('winners_section')}>
              <TextField label="Section title" value={winnersSec.section_title} onChange={(v) => update('winners_section', (w) => ({ ...w, section_title: v }))} />
              <TextField label="Winner card prefix (e.g. 'Winner')" value={winnersSec.winner_prefix} onChange={(v) => update('winners_section', (w) => ({ ...w, winner_prefix: v }))} />
              <TextField label="Winners shown per page" type="number" value={winnersSec.per_page} onChange={(v) => update('winners_section', (w) => ({ ...w, per_page: parseInt(v) || 12 }))} />
              <ToggleField label="Section visible" checked={winnersSec.visibility} onChange={(v) => update('winners_section', (w) => ({ ...w, visibility: v }))} />
              <ToggleField label="Mask Sugo IDs" checked={winnersSec.mask_ids} onChange={(v) => update('winners_section', (w) => ({ ...w, mask_ids: v }))} />
              <ToggleField label="Show username" checked={winnersSec.show_username} onChange={(v) => update('winners_section', (w) => ({ ...w, show_username: v }))} />
              <ToggleField label="Show prize" checked={winnersSec.show_prize} onChange={(v) => update('winners_section', (w) => ({ ...w, show_prize: v }))} />
              <ToggleField label="Show draw date" checked={winnersSec.show_draw_date} onChange={(v) => update('winners_section', (w) => ({ ...w, show_draw_date: v }))} />
            </SectionCard>
          </TabsContent>

          {/* BRANDING */}
          <TabsContent value="branding">
            <SectionCard title="Branding" sectionKey="branding" saving={saving === 'branding'} onSave={() => saveSection('branding')} onReset={() => setResetKey('branding')}>
              <p className="text-xs text-[#D4AF37] bg-[#080808] border border-[#D4AF37]/20 rounded p-3">
                These colours and fonts are applied site-wide via CSS variables. Save to update the live site. Reset restores the default black/gold/pink/white palette with Manrope & Inter fonts. Custom heading/body fonts are auto-loaded from Google Fonts.
              </p>
              <ImagePicker label="Website logo" value={branding.logo_url} onChange={(v) => update('branding', (b) => ({ ...b, logo_url: v }))} testId="branding-logo" />
              <ImagePicker label="Favicon" value={branding.favicon_url} onChange={(v) => update('branding', (b) => ({ ...b, favicon_url: v }))} testId="branding-favicon" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="Primary gold (hex)" value={branding.primary_gold} onChange={(v) => update('branding', (b) => ({ ...b, primary_gold: v }))} />
                <TextField label="Pink accent (hex)" value={branding.pink_accent} onChange={(v) => update('branding', (b) => ({ ...b, pink_accent: v }))} />
                <TextField label="Background (hex)" value={branding.background} onChange={(v) => update('branding', (b) => ({ ...b, background: v }))} />
                <TextField label="Card background (hex)" value={branding.card_bg} onChange={(v) => update('branding', (b) => ({ ...b, card_bg: v }))} />
                <TextField label="Primary text (hex)" value={branding.text_primary} onChange={(v) => update('branding', (b) => ({ ...b, text_primary: v }))} />
                <TextField label="Secondary text (hex)" value={branding.text_secondary} onChange={(v) => update('branding', (b) => ({ ...b, text_secondary: v }))} />
                <TextField label="Heading font" value={branding.heading_font} onChange={(v) => update('branding', (b) => ({ ...b, heading_font: v }))} />
                <TextField label="Body font" value={branding.body_font} onChange={(v) => update('branding', (b) => ({ ...b, body_font: v }))} />
              </div>
            </SectionCard>
          </TabsContent>

          {/* FOOTER */}
          <TabsContent value="footer">
            <SectionCard title="Footer" sectionKey="footer" saving={saving === 'footer'} onSave={() => saveSection('footer')} onReset={() => setResetKey('footer')}>
              <TextField label="Copyright text" value={footer.copyright} onChange={(v) => update('footer', (f) => ({ ...f, copyright: v }))} testId="input-footer-copyright" />
              <TextField label="Contact label" value={footer.contact_label} onChange={(v) => update('footer', (f) => ({ ...f, contact_label: v }))} />
              <TextField label="WhatsApp label" value={footer.whatsapp_label} onChange={(v) => update('footer', (f) => ({ ...f, whatsapp_label: v }))} />
              <TextField type="url" label="WhatsApp URL" value={footer.whatsapp_url} onChange={(v) => update('footer', (f) => ({ ...f, whatsapp_url: v }))} testId="input-footer-whatsapp" />
              <ImagePicker label="Footer logo" value={footer.logo_url} onChange={(v) => update('footer', (f) => ({ ...f, logo_url: v }))} testId="footer-logo" />
            </SectionCard>
          </TabsContent>

          {/* SEO */}
          <TabsContent value="seo">
            <SectionCard title="SEO & Social Sharing" sectionKey="seo" saving={saving === 'seo'} onSave={() => saveSection('seo')} onReset={() => setResetKey('seo')}>
              <TextField label="Page title" value={seo.page_title} onChange={(v) => update('seo', (s) => ({ ...s, page_title: v }))} testId="input-seo-title" />
              <TextArea label="Meta description" value={seo.meta_description} onChange={(v) => update('seo', (s) => ({ ...s, meta_description: v }))} />
              <TextField label="Open Graph title" value={seo.og_title} onChange={(v) => update('seo', (s) => ({ ...s, og_title: v }))} />
              <TextArea label="Open Graph description" value={seo.og_description} onChange={(v) => update('seo', (s) => ({ ...s, og_description: v }))} />
              <ImagePicker label="Social sharing image" value={seo.og_image_url} onChange={(v) => update('seo', (s) => ({ ...s, og_image_url: v }))} testId="seo-og-image" />
            </SectionCard>
          </TabsContent>

          {/* MEDIA */}
          <TabsContent value="media">
            <MediaLibraryTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Reset confirmation */}
      <AlertDialog open={!!resetKey} onOpenChange={(open) => !open && setResetKey(null)}>
        <AlertDialogContent className="bg-[#121212] border border-red-500/40 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Reset to default?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0A0A0]">
              This will discard all your customisations for <span className="text-red-400 font-bold">{resetKey}</span> and restore the seed defaults.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#080808] border-[#A0A0A0]/30 text-white hover:bg-[#121212]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReset} className="bg-red-600 text-white hover:bg-red-700" data-testid="btn-confirm-reset">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ------------------ Media library tab ------------------ */

const MediaLibraryTab = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/media');
      setMedia(data);
    } catch { toast.error('Failed to load media'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('alt_text', file.name);
      try {
        await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Failed: ${file.name}`);
      }
    }
    load();
    e.target.value = '';
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/media/${deleteId}`);
      toast.success('Deleted');
      setDeleteId(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const updateAlt = async (id, alt) => {
    try {
      await api.put(`/media/${id}`, { alt_text: alt });
      toast.success('Alt text saved');
    } catch { toast.error('Failed'); }
  };

  return (
    <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6" data-testid="media-library-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">Media Library</h2>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="media-upload-input" />
          <Button onClick={() => fileRef.current?.click()} className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]" data-testid="btn-media-upload">
            <Upload className="w-4 h-4 mr-2" />Upload images
          </Button>
        </div>
      </div>
      <p className="text-xs text-[#A0A0A0] mb-4">Allowed: JPEG, PNG, WEBP, GIF, SVG. Max 5 MB each.</p>

      {loading ? (
        <div className="text-[#A0A0A0]">Loading...</div>
      ) : media.length === 0 ? (
        <div className="text-[#A0A0A0] text-center py-8">No media uploaded yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="media-grid">
          {media.map((m) => (
            <div key={m.media_id} className="bg-[#080808] border border-[#D4AF37]/10 rounded-lg overflow-hidden" data-testid={`media-item-${m.media_id}`}>
              <div className="aspect-square bg-black/40">
                <img src={`${API_URL}${m.url}`} alt={m.alt_text} className="w-full h-full object-cover" />
              </div>
              <div className="p-2 space-y-1">
                <input
                  defaultValue={m.alt_text}
                  onBlur={(e) => updateAlt(m.media_id, e.target.value)}
                  placeholder="alt text"
                  className="w-full text-xs bg-[#080808] border border-[#D4AF37]/20 rounded px-1 py-1 text-white"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#A0A0A0] truncate flex-1" title={m.original_name}>{m.original_name}</span>
                  <button onClick={() => setDeleteId(m.media_id)} className="text-red-500 hover:text-red-400" data-testid={`btn-delete-media-${m.media_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <input readOnly value={`${API_URL}${m.url}`} className="flex-1 text-[10px] bg-transparent text-[#A0A0A0] truncate" />
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${API_URL}${m.url}`); toast.success('URL copied'); }}
                    className="text-[10px] text-[#D4AF37]"
                  >Copy</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#121212] border border-red-500/40 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this image?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0A0A0]">
              This will remove the file. Any content still referencing this URL will show a broken image.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#080808] border-[#A0A0A0]/30 text-white hover:bg-[#121212]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="btn-confirm-delete-media">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ContentManagement;
